import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import Polygon from "@arcgis/core/geometry/Polygon";
import Polyline from "@arcgis/core/geometry/Polyline";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import { SimpleLineSymbol, TextSymbol } from "@arcgis/core/symbols";
import globalLayers from "../../../Utils/CustomerMaps/Maps/GlobaLMap";
import { getSimpleLineSymboleMarker, getTextSymbolObject } from "../../../Utils/CustomerMaps/MarkerSymbols/MarkerSymbols";

/**
 * Measurement guide lines (arrow line + length label) shown while drawing a roof polygon.
 *
 * The guides are always derived from the current vertex list, so undo / redo (ctrl+z / ctrl+y),
 * cancel and completion keep them in sync. Note: while drawing, ArcGIS reports ctrl+z / ctrl+y through the
 * Sketch "undo" / "redo" events (not as create "vertex-remove"), so those must be routed to `syncPolygonGuides`. Everything is synchronous to avoid the duplicate /
 * orphaned graphics produced by overlapping async cursor updates.
 */

const LINE_OFFSET_M = 2;
const LABEL_OFFSET_M = 1;
const MIN_SEGMENT_M = 0.01;

type Guide = { line: Graphic; label: Graphic };

let vertices: number[][] = [];
let segments: Guide[] = [];
let live: Guide | null = null;
let lastCursor: number[] | null = null;
let titleName = "";
let spatialReference: SpatialReference | null = null;

const toXY = (coords: number[]) => [coords[0], coords[1]];

const sameXY = (a: number[], b: number[]) => a[0] === b[0] && a[1] === b[1];

const segmentLength = (line: Polyline) => {
    const sr = line.spatialReference;
    return sr && (sr.isWGS84 || sr.isWebMercator)
        ? geometryEngine.geodesicLength(line, "meters")
        : geometryEngine.planarLength(line, "meters");
};

const safeOffset = (geometry: Polyline, distance: number) => {
    try {
        return geometryEngine.offset(geometry, distance, "meters") as Polyline | null;
    } catch (e) {
        return null;
    }
};

const midPoint = (line: Polyline) => {
    const path = line.paths[0];
    const a = path[0];
    const b = path[path.length - 1];
    return new Point({ x: (a[0] + b[0]) / 2, y: (a[1] + b[1]) / 2, spatialReference: line.spatialReference });
};

/** Pick the offset side that falls outside the shape so labels never sit on top of the roof. */
const outwardSign = (line: Polyline, ring: number[][]) => {
    if (ring.length < 3) return 1;
    try {
        const polygon = new Polygon({ rings: [[...ring, ring[0]]], spatialReference: line.spatialReference });
        const probe = safeOffset(line, LINE_OFFSET_M);
        if (probe && geometryEngine.contains(polygon, midPoint(probe))) return -1;
    } catch (e) {
        // fall back to the default side
    }
    return 1;
};

const buildGuideGeometry = (start: number[], end: number[], ring: number[][]) => {
    if (!spatialReference || sameXY(start, end)) return null;

    const baseLine = new Polyline({ paths: [[end, start]], spatialReference });
    const length = segmentLength(baseLine);
    if (!isFinite(length) || length < MIN_SEGMENT_M) return null;

    const sign = outwardSign(baseLine, ring);
    const line = safeOffset(baseLine, sign * LINE_OFFSET_M) ?? baseLine;
    const labelLine = safeOffset(line, sign * LABEL_OFFSET_M) ?? line;

    return { line, labelPoint: midPoint(labelLine), length };
};

const ensureOnLayer = (graphic: Graphic) => {
    const layer = globalLayers.sketchLayers;
    if (layer && !layer.graphics.includes(graphic)) {
        layer.graphics.add(graphic);
    }
};

const removeGuide = (guide: Guide | null) => {
    if (!guide) return;
    globalLayers.sketchLayers?.graphics.removeMany([guide.line, guide.label]);
};

/** Create or update a guide between two vertices; returns null (and removes it) when the segment is degenerate. */
const upsertGuide = (guide: Guide | null, start: number[], end: number[], ring: number[][]): Guide | null => {
    const geom = buildGuideGeometry(start, end, ring);
    if (!geom) {
        removeGuide(guide);
        return null;
    }

    const textSymbol = new TextSymbol(getTextSymbolObject(geom.length, [225, 225, 225]) as any);

    if (!guide) {
        const attributes = { name: titleName };
        guide = {
            line: new Graphic({ geometry: geom.line, symbol: getSimpleLineSymboleMarker(SimpleLineSymbol) as any, attributes }),
            label: new Graphic({ geometry: geom.labelPoint, symbol: textSymbol, attributes: { ...attributes } }),
        };
        // `title` is used across the app to find polylines belonging to a roof
        (guide.line as any).title = titleName;
        (guide.label as any).title = titleName;
    } else {
        guide.line.geometry = geom.line;
        guide.label.geometry = geom.labelPoint;
        guide.label.symbol = textSymbol;
    }

    guide.line.visible = true;
    guide.label.visible = true;
    ensureOnLayer(guide.line);
    ensureOnLayer(guide.label);
    return guide;
};

/** Rebuild the committed edge guides so they exactly match the current vertex list. */
const redrawSegments = (closed: boolean) => {
    const pairs: [number[], number[]][] = [];
    for (let i = 0; i < vertices.length - 1; i++) {
        pairs.push([vertices[i], vertices[i + 1]]);
    }
    if (closed && vertices.length > 2) {
        pairs.push([vertices[vertices.length - 1], vertices[0]]);
    }

    const next: Guide[] = [];
    pairs.forEach(([start, end], i) => {
        const guide = upsertGuide(segments[i] ?? null, start, end, vertices);
        if (guide) next.push(guide);
    });
    // drop guides for segments that no longer exist (e.g. after undo)
    segments.slice(pairs.length).forEach(removeGuide);
    segments = next;
};

const updateLive = () => {
    if (!lastCursor || vertices.length === 0) {
        removeGuide(live);
        live = null;
        return;
    }
    live = upsertGuide(live, vertices[vertices.length - 1], lastCursor, [...vertices, lastCursor]);
};

/** Remove every guide of the in-progress drawing and forget its state. */
export const resetPolygonGuides = () => {
    segments.forEach(removeGuide);
    removeGuide(live);
    segments = [];
    live = null;
    vertices = [];
    lastCursor = null;
};

/** Forget the in-progress state but keep the drawn guides on the layer (used after completion). */
const detachPolygonGuides = () => {
    segments = [];
    live = null;
    vertices = [];
    lastCursor = null;
};

const almostEqual = (a: number[], b: number[]) => Math.abs(a[0] - b[0]) < 1e-6 && Math.abs(a[1] - b[1]) < 1e-6;

/**
 * Committed (clicked) vertices of the polygon currently being drawn, read from the active draw tool.
 * This is not part of the public API, hence the defensive access; returns null when unavailable.
 */
const getCommittedVertices = (): number[][] | null => {
    try {
        const committed = (globalLayers.sketchVM as any)?.viewModel?.activeComponent?.drawOperation?.committedVertices;
        return Array.isArray(committed) ? committed.map(toXY) : null;
    } catch (e) {
        return null;
    }
};

/** Fallback: the drawing graphic's ring also contains the cursor (staged) vertex, which is dropped here. */
const getVerticesFromDrawingGraphic = (graphic: __esri.Graphic | undefined) => {
    const ring = getRing(graphic);
    if (!ring) return null;
    if (lastCursor && ring.length && almostEqual(ring[ring.length - 1], lastCursor)) ring.pop();
    return ring;
};

const setVertices = (next: number[][]) => {
    vertices = next;
    redrawSegments(false);
    updateLive();
};

const sameVertices = (a: number[][], b: number[][]) => a.length === b.length && a.every((v, i) => almostEqual(v, b[i]));

/**
 * Re-sync the guides with the vertices actually committed in the draw operation.
 * Call it from the Sketch "undo" / "redo" events.
 */
export const syncPolygonGuides = (event: { graphics?: __esri.Graphic[]; tool?: string }) => {
    if (event.tool !== "polygon" || !spatialReference) return;
    const next = getCommittedVertices() ?? getVerticesFromDrawingGraphic(event.graphics?.[0]);
    if (!next) return;
    setVertices(next);
};

const getRing = (graphic: __esri.Graphic | undefined) => {
    const ring = (graphic?.geometry as Polygon | undefined)?.rings?.[0];
    if (!ring || ring.length === 0) return null;
    const coords = ring.map(toXY);
    if (coords.length > 1 && sameXY(coords[0], coords[coords.length - 1])) coords.pop();
    return coords;
};

export const handlePolygonGuideEvent = (event: __esri.SketchCreateEvent, title: string | undefined) => {
    if (event.tool !== "polygon") return;

    if (event.state === "start") {
        resetPolygonGuides();
        titleName = title ?? "";
        spatialReference = (event.graphic?.geometry?.spatialReference ?? globalLayers.view?.spatialReference ?? null) as SpatialReference | null;
    }

    if (event.state === "cancel") {
        resetPolygonGuides();
        return;
    }

    if (event.state === "complete") {
        const ring = getRing(event.graphic);
        if (ring) {
            spatialReference = (event.graphic.geometry?.spatialReference ?? spatialReference) as SpatialReference | null;
            vertices = ring;
        }
        removeGuide(live);
        live = null;
        redrawSegments(true);
        detachPolygonGuides();
        return;
    }

    const info = event.toolEventInfo as __esri.VertexAddEventInfo | __esri.VertexRemoveEventInfo | __esri.CursorUpdateEventInfo | undefined;
    if (!info) return;

    switch (info.type) {
        case "vertex-add": {
            const added = [...(info.vertices ?? [])].sort((a, b) => a.vertexIndex - b.vertexIndex);
            if (added.length) {
                added.forEach(v => {
                    // a polygon being drawn only grows at its end: anything at/after the new index is stale
                    if (v.vertexIndex >= 0 && v.vertexIndex < vertices.length) vertices.length = v.vertexIndex;
                    vertices.push(toXY(v.coordinates));
                });
            } else if (Array.isArray((info as any).added?.[0])) {
                vertices.push(toXY((info as any).added[0]));
            }
            redrawSegments(false);
            lastCursor = vertices[vertices.length - 1] ?? null;
            updateLive();
            break;
        }
        case "vertex-remove": {
            const removed = [...(info.vertices ?? [])].sort((a, b) => b.vertexIndex - a.vertexIndex);
            if (removed.length) {
                removed.forEach(v => {
                    if (v.vertexIndex >= 0 && v.vertexIndex < vertices.length) {
                        vertices.splice(v.vertexIndex, 1);
                    } else {
                        vertices.pop();
                    }
                });
            } else {
                vertices.pop();
            }
            redrawSegments(false);
            updateLive();
            break;
        }
        case "cursor-update": {
            lastCursor = toXY(info.coordinates);
            // safety net in case an undo/redo was missed
            const committed = getCommittedVertices();
            if (committed && !sameVertices(committed, vertices)) {
                setVertices(committed);
                break;
            }
            updateLive();
            break;
        }
    }
};
